/**
 * The remote appearance request letter (Lloyd, 16 Sep 2026).
 *
 * One document, not a pair: his template has no proposed order. And there is no send method here on
 * purpose — he reversed his own first instruction and asked for a button rather than an automation,
 * so the letter comes back as a PDF and a person decides what to do with it.
 */

export type RemoteAppearancePolicy = 'granted' | 'denied' | 'no-local-form';

export interface RemoteAppearanceBlockerDto {
  field: string | null;
  reason: string;
}

export interface RemoteAppearanceReadinessDto {
  canGenerate?: boolean;
  blockers?: RemoteAppearanceBlockerDto[];
  /** Things to know before sending. Never refusals. */
  warnings?: string[];
  courtPolicy?: RemoteAppearancePolicy | null;
  courtPolicyNote?: string | null;
  courtGuid?: string | null;
}

export interface RemoteAppearanceLetterDto {
  fileName?: string;
  pdfBase64?: string;
  warnings?: string[];
}

export class RemoteAppearanceBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: RemoteAppearanceBlockerDto[],
  ) {
    super(message);
    this.name = 'RemoteAppearanceBlockedError';
  }
}

export default class RemoteAppearanceApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    params: { settingDate?: string; settingType?: string; courtName?: string },
  ): Promise<RemoteAppearanceReadinessDto> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value);
    }
    const queryString = query.toString();
    const response = await fetch(
      `${RemoteAppearanceApi.host()}/api/v1/manager/ticket/${ticketGuid}/remote-appearance/readiness${
        queryString ? `?${queryString}` : ''
      }`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error('Failed to check whether this case is ready for a remote appearance request');
    }
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: { settingDate: string; settingType?: string; courtName?: string },
  ): Promise<RemoteAppearanceLetterDto> {
    const response = await fetch(
      `${RemoteAppearanceApi.host()}/api/v1/manager/ticket/${ticketGuid}/remote-appearance`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new RemoteAppearanceBlockedError(
        err.message ?? 'This case is not ready',
        err.blockers ?? [],
      );
    }
    if (!response.ok) {
      throw new Error('Failed to generate the remote appearance request');
    }
    return response.json();
  }

  /** Record what a court said, so the firm builds the running list Lloyd asked for. */
  static async recordCourtPolicy(
    courtGuid: string,
    policy: RemoteAppearancePolicy | null,
    note?: string | null,
  ): Promise<void> {
    const response = await fetch(
      `${RemoteAppearanceApi.host()}/api/v1/court/${courtGuid}/remote-appearance`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ policy, note: note ?? null }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to record the court's answer");
    }
  }
}
